/*
  UnifiedSearch — /search?q=… page

  Searches across archetypes, essays, and research frameworks via
  GET /api/search. Results grouped, one-glance scannable.
*/
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, ArrowRight, BookOpen, Sparkles, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { SeoHead } from "@/components/SeoHead";

export function UnifiedSearch() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSearch = async (term) => {
    const t = (term || "").trim();
    if (!t) { setData(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/search?q=${encodeURIComponent(t)}`);
      setData(r.data);
    } catch { setData({ error: true }); } finally { setLoading(false); }
  };

  useEffect(() => { if (initial) runSearch(initial); }, [initial]);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams(q ? { q } : {});
    runSearch(q);
  };

  return (
    <div data-testid="search-page" className="overflow-x-hidden">
      <SeoHead
        title="Search SugarSense — archetypes, essays, research"
        description="Search across the 41 relationship archetypes, the editorial library, and the behavioural frameworks SugarSense uses."
        path="/search"
      />

      <section className="ss-bg-radiant border-b border-champagne">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Search</p>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl text-cocoa leading-tight">
            One library, <span className="italic brand-shine">one search.</span>
          </h1>
          <form onSubmit={onSubmit} className="mt-7 flex gap-2" data-testid="search-form">
            <div className="flex-1 relative">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-cocoa/40" size={16} />
              <input
                data-testid="search-input"
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Try a name, a pattern, a feeling — e.g. 'future faker', 'silence', 'attachment'"
                type="search" autoFocus
                className="w-full bg-ivory border border-champagne rounded-full h-12 pl-11 pr-5 text-cocoa placeholder-cocoa/40 focus:border-mauve outline-none"
              />
            </div>
            <button data-testid="search-submit" type="submit" className="ss-pill ss-pill-primary h-12 px-6">Search</button>
          </form>
          <p className="mt-4 text-xs tracking-widest uppercase text-cocoa/40">Archetypes · Editorial · Research frameworks</p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {loading && <p className="text-cocoa/50 text-sm italic">Reading the library…</p>}
        {!loading && !data && (
          <div data-testid="search-empty-state" className="text-cocoa/55 text-sm">
            <p className="italic">Start typing to search the archetype library, editorial essays, and research frameworks.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["future faker", "silence", "attachment", "control", "money", "withdrawal"].map((sug) => (
                <button key={sug} data-testid={`search-suggest-${sug.replace(/\s+/g, "-")}`}
                  onClick={() => { setQ(sug); setParams({ q: sug }); runSearch(sug); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-champagne/60 text-cocoa/70 hover:bg-cocoa hover:text-ivory transition-colors">
                  {sug}
                </button>
              ))}
            </div>
          </div>
        )}
        {!loading && data && data.error && <p className="text-rose text-sm">Search failed — please try again.</p>}
        {!loading && data && !data.error && (
          <>
            {data.total === 0 && (
              <div data-testid="search-no-results" className="text-center py-10">
                <p className="text-cocoa/55">No matches for <em className="text-cocoa">{data.q}</em>.</p>
                <p className="mt-3 text-cocoa/45 text-sm">Try a different phrasing, or <Link className="underline" to="/archetypes">browse the archetypes</Link>.</p>
              </div>
            )}

            {data.archetypes?.length > 0 && (
              <Group testid="search-group-archetypes" icon={Heart} label="Archetypes" count={data.archetypes.length}>
                {data.archetypes.map((a) => (
                  <Link key={a.key} to={`/archetypes/${a.key}`} data-testid={`search-arch-${a.key}`} className="ss-card-premium p-5 block hover:bg-blush/15 transition-colors">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{a.category}</p>
                    <h3 className="mt-1 font-heading text-xl text-cocoa">{a.name}</h3>
                    <p className="mt-2 text-cocoa/65 text-sm leading-relaxed line-clamp-2">{a.description}</p>
                  </Link>
                ))}
              </Group>
            )}

            {data.essays?.length > 0 && (
              <Group testid="search-group-essays" icon={BookOpen} label="Essays" count={data.essays.length}>
                {data.essays.map((e) => (
                  <Link key={e.slug} to={`/essays/${e.slug}`} data-testid={`search-essay-${e.slug}`} className="ss-card p-5 block hover:bg-blush/15 transition-colors">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{e.category} · {e.reading_time_min || 3} min read</p>
                    <h3 className="mt-1 font-heading text-xl text-cocoa">{e.title}</h3>
                    {e.subtitle && <p className="mt-1 font-heading italic text-cocoa/65">{e.subtitle}</p>}
                  </Link>
                ))}
              </Group>
            )}

            {data.frameworks?.length > 0 && (
              <Group testid="search-group-frameworks" icon={Sparkles} label="Research frameworks" count={data.frameworks.length}>
                {data.frameworks.map((f) => (
                  <Link key={f.key} to={`/research#${f.key}`} data-testid={`search-fw-${f.key}`} className="ss-card p-5 block hover:bg-blush/15 transition-colors">
                    <h3 className="font-heading text-xl text-cocoa">{f.name}</h3>
                    <p className="mt-1 font-heading italic text-cocoa/65">{f.sub}</p>
                  </Link>
                ))}
              </Group>
            )}

            {data.total > 0 && (
              <div className="mt-12 text-center">
                <p className="text-cocoa/55 text-sm">Looking for something deeper?</p>
                <Link to="/register" className="mt-3 inline-block">
                  <button className="ss-pill ss-pill-primary h-11 px-6">Ask SugarSense directly <ArrowRight size={14} /></button>
                </Link>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function Group({ testid, icon: Icon, label, count, children }) {
  return (
    <section data-testid={testid} className="mb-12">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="text-mauve" size={16} strokeWidth={1.5} />
        <h2 className="font-heading text-lg text-cocoa">{label}</h2>
        <span className="text-xs text-cocoa/45">· {count}</span>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
